import {
	Alert,
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Heading,
	Icons,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
} from "@sembark-travel/ui-base"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	DateRangePickerField,
	Form,
	SelectInputField,
	SubmissionError,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { formatDate, startOf } from "@sembark-travel/datetime-utils"
import { useEffect, useState } from "react"
import { useXHR } from "@sembark-travel/xhr"
import * as Validator from "yup"
import { IHotel } from "../Hotels"
import {
	AXISROOMS_ACTIONS,
	AXISROOMS_OCCUPANCIES,
	AXISROOMS_RESTRICTION_TYPES,
	TAxisRoomsAction,
	TAxisRoomsResponse,
	sendAxisRoomsRequest,
} from "./store"

function toApiDate(date: Date | string): string {
	return typeof date === "string" ? date : formatDate(date, "YYYY-MM-DD")
}

/**
 * Every form on this page live in the same document, and the form library
 * derive each field's DOM id from its name. So each form prefix its field
 * names, otherwise a label would focus the matching field of the first form.
 */
function dateRangeValidators(prefix: string) {
	return {
		[`${prefix}start_date`]: Validator.date().required(
			"Please select a start date"
		),
		[`${prefix}end_date`]: Validator.date()
			.required("Please select an end date")
			.min(
				Validator.ref(`${prefix}start_date`),
				"End date must be on or after the start date"
			),
	}
}

/**
 * Imitate the AxisRooms channel manager for a hotel by making the same calls
 * AxisRooms would make. Only for testing the integration.
 *
 * Nothing here hold a credential: the calls go to our own simulator endpoint,
 * which authenticate as AxisRooms on the way through.
 */
export function AxisRoomsSimulator({ hotel }: { hotel: IHotel }) {
	if (!hotel.uid) {
		return <MissingHotelCode />
	}
	return (
		<Stack gap="6">
			<Alert status="info" title="Sandbox">
				Every form below make the same call AxisRooms would make to Sembark. Use
				it to verify the integration without a real AxisRooms account.
			</Alert>
			<Stack gap="2">
				<Heading as="h5">Fetch</Heading>
				<Text fontSize="sm" color="muted">
					What AxisRooms read before it price anything. The rooms and rate plans
					it answer with are what the pushes below refer to.
				</Text>
			</Stack>
			{/* no `gap` here: it switches the Grid to no-gutters and drop the
			Col padding, which push the columns past 100% and stack them */}
			<Grid>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<ProductInfoForm hotel={hotel} />
				</Col>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<RatePlanInfoForm hotel={hotel} />
				</Col>
			</Grid>
			<Stack gap="2">
				<Heading as="h5">Push</Heading>
				<Text fontSize="sm" color="muted">
					These are taken and then applied by a worker, so they answer with an
					acknowledgement rather than the updated data.
				</Text>
			</Stack>
			<Grid>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<InventoryUpdateForm hotel={hotel} />
				</Col>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<RateUpdateForm hotel={hotel} />
				</Col>
				<Col xs={12} sm={6} md={4} paddingBottom="6">
					<RestrictionUpdateForm hotel={hotel} />
				</Col>
			</Grid>
		</Stack>
	)
}

/**
 * The simulator's forms in a dialog, so they can be used from a hotel's
 * calendar. Same forms as the simulator page, grouped into Update ARI and
 * Fetch ARI with one form visible at a time.
 */
export function AxisRoomsSyncAriDialog({
	hotel,
	open,
	onClose,
}: {
	hotel: IHotel
	open: boolean
	onClose: () => void
}) {
	return (
		<Dialog open={open} onClose={onClose} title="Sync ARI" md>
			<Dialog.Body paddingX="0" paddingY="0">
				<SyncAriTabs hotel={hotel} />
			</Dialog.Body>
		</Dialog>
	)
}

function SyncAriTabs({ hotel }: { hotel: IHotel }) {
	if (!hotel.uid) {
		return <MissingHotelCode />
	}

	return (
		<Tabs layout="col" defaultActive="fetch">
			<TabsList>
				<TabItem id="fetch">Fetch ARI</TabItem>
				<TabItem id="update">Update ARI</TabItem>
			</TabsList>
			<TabContent id="fetch">
				<Tabs defaultActive="get_product_info">
					<TabsList>
						<TabItem id="get_product_info">Get Product Info</TabItem>
						<TabItem id="get_rate_plan_info">Get Rate Plan Info</TabItem>
					</TabsList>
					<TabContent id="get_product_info">
						<ProductInfoForm hotel={hotel} />
					</TabContent>
					<TabContent id="get_rate_plan_info">
						<RatePlanInfoForm hotel={hotel} />
					</TabContent>
				</Tabs>
			</TabContent>
			<TabContent id="update">
				<Tabs defaultActive="update_inventory">
					<TabsList>
						<TabItem id="update_inventory">Update Inventory</TabItem>
						<TabItem id="update_rate">Update Rate</TabItem>
						<TabItem id="update_restrictions">Update Restrictions</TabItem>
					</TabsList>
					<TabContent id="update_inventory">
						<InventoryUpdateForm hotel={hotel} />
					</TabContent>
					<TabContent id="update_rate">
						<RateUpdateForm hotel={hotel} />
					</TabContent>
					<TabContent id="update_restrictions">
						<RestrictionUpdateForm hotel={hotel} />
					</TabContent>
				</Tabs>
			</TabContent>
		</Tabs>
	)
}

function MissingHotelCode() {
	return (
		<Alert status="error" title="Hotel has no code">
			AxisRooms know a property by the hotel's code, and this hotel has none.
			Please reload the page, and contact Sembark support if it persists.
		</Alert>
	)
}

function SimulatorResponse({
	response,
}: {
	response: TAxisRoomsResponse | undefined
}) {
	if (!response) return null
	const body = response.body || "(empty response)"
	return (
		<Stack gap="1">
			<Text
				fontSize="sm"
				fontWeight="semibold"
				color={response.ok ? "success" : "danger"}
			>
				HTTP {response.status}
			</Text>
			<Box position="relative">
				<Box
					as="pre"
					padding="2"
					bgColor="subtle"
					rounded="md"
					fontSize="xs"
					overflow="auto"
				>
					{body}
				</Box>
				<Box position="absolute" style={{ top: "4px", right: "4px" }}>
					<CopyToClipboard>
						{({ copyTextToClipboard }) => (
							<CopyToClipboardButton
								size="sm"
								iconOnly
								title="Copy the response"
								onClick={() => copyTextToClipboard(body)}
							>
								<Icons.Duplicate />
							</CopyToClipboardButton>
						)}
					</CopyToClipboard>
				</Box>
			</Box>
		</Stack>
	)
}

function SimulatorCard({
	title,
	action,
	children,
}: {
	title: string
	action: TAxisRoomsAction
	children: React.ReactNode
}) {
	return (
		<Box padding="4" borderWidth="1" rounded="md" bgColor="default">
			<Stack gap="4">
				<Stack gap="1">
					<Heading as="h5">{title}</Heading>
					<Text fontSize="sm" color="muted">
						/{action}
					</Text>
				</Stack>
				{children}
			</Stack>
		</Box>
	)
}

function MissingOtaConfiguration({ what }: { what: string }) {
	return (
		<Alert status="warning" title={`No ${what} for this hotel`}>
			Please configure the OTA room types, meal plans, and rate plans of this
			hotel before simulating this call.
		</Alert>
	)
}

function DateRangeFields({ prefix }: { prefix: string }) {
	return (
		<DateRangePickerField
			label="Dates"
			fromName={`${prefix}start_date`}
			toName={`${prefix}end_date`}
			rangePresets={["Today", "Next 7 Days"]}
			required
		/>
	)
}

function RoomTypeField({ hotel, prefix }: { hotel: IHotel; prefix: string }) {
	const roomTypes = hotel.ota_room_types || []
	return (
		<SelectInputField label="Room" name={`${prefix}room_id`} required>
			<option value="">Select a room</option>
			{roomTypes.map((roomType) => (
				<option value={roomType.id} key={roomType.id}>
					{roomType.name} (#{roomType.id})
				</option>
			))}
		</SelectInputField>
	)
}

/**
 * Rate plans of the room picked above, and only those: a rate plan stand for
 * one room sold under one meal plan, so the api refuse one that belong to
 * another room.
 *
 * Changing the room drop whatever was picked before, so a rate plan of the
 * room that was selected a moment ago can not be sent.
 */
function RatePlanField({ hotel, prefix }: { hotel: IHotel; prefix: string }) {
	const { value: roomId } = useFieldValue<string>(`${prefix}room_id`)
	const { value: ratePlanId, onChange: changeRatePlan } = useFieldValue<string>(
		`${prefix}rate_plan_id`
	)
	const ratePlans = (hotel.ota_rate_plans || []).filter(
		(ratePlan) => String(ratePlan.ota_room_type_id) === String(roomId)
	)
	const isPickedOneOfTheRooms = ratePlans.some(
		(ratePlan) => String(ratePlan.id) === String(ratePlanId)
	)

	useEffect(() => {
		if (ratePlanId && !isPickedOneOfTheRooms) {
			changeRatePlan("")
		}
	}, [ratePlanId, isPickedOneOfTheRooms, changeRatePlan])

	if (!roomId) {
		return (
			<SelectInputField
				label="Rate Plan"
				name={`${prefix}rate_plan_id`}
				required
				disabled
			>
				<option value="">Select a room first</option>
			</SelectInputField>
		)
	}

	return (
		<SelectInputField label="Rate Plan" name={`${prefix}rate_plan_id`} required>
			<option value="">
				{ratePlans.length
					? "Select a rate plan"
					: "This room has no rate plans"}
			</option>
			{ratePlans.map((ratePlan) => (
				<option value={ratePlan.id} key={ratePlan.id}>
					{ratePlan.name} (#{ratePlan.id})
				</option>
			))}
		</SelectInputField>
	)
}

function ProductInfoForm({ hotel }: { hotel: IHotel }) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TAxisRoomsResponse>()
	return (
		<SimulatorCard
			title="Get Product Info"
			action={AXISROOMS_ACTIONS.PRODUCT_INFO}
		>
			<Form<Record<string, never>>
				initialValues={{}}
				onSubmit={withServerErrors(async () => {
					setResponse(
						await sendAxisRoomsRequest({
							xhr,
							action: AXISROOMS_ACTIONS.PRODUCT_INFO,
							payload: { propertyId: hotel.uid },
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<Text fontSize="sm" color="muted">
								Answer with the rooms this property sell.
							</Text>
							<SubmissionError />
							<Divider sm />
							<Button type="submit" disabled={submitting}>
								{submitting ? "Fetching..." : "Fetch Rooms"}
							</Button>
							<SimulatorResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

const RATE_PLAN_INFO_PREFIX = "rate_plan_info_"

type TRatePlanInfoParams = {
	rate_plan_info_room_id: string
}

const ratePlanInfoValidator = Validator.object().shape({
	rate_plan_info_room_id: Validator.string().required("Please select a room"),
})

function RatePlanInfoForm({ hotel }: { hotel: IHotel }) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TAxisRoomsResponse>()
	const [initialValues] = useState<TRatePlanInfoParams>(() => ({
		rate_plan_info_room_id: "",
	}))
	if (!(hotel.ota_room_types || []).length) {
		return (
			<SimulatorCard
				title="Get Rate Plan Info"
				action={AXISROOMS_ACTIONS.RATE_PLAN_INFO}
			>
				<MissingOtaConfiguration what="rooms" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard
			title="Get Rate Plan Info"
			action={AXISROOMS_ACTIONS.RATE_PLAN_INFO}
		>
			<Form<TRatePlanInfoParams>
				initialValues={initialValues}
				validate={validateFormValues(ratePlanInfoValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendAxisRoomsRequest({
							xhr,
							action: AXISROOMS_ACTIONS.RATE_PLAN_INFO,
							payload: {
								propertyId: hotel.uid,
								roomId: values.rate_plan_info_room_id,
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RoomTypeField hotel={hotel} prefix={RATE_PLAN_INFO_PREFIX} />
							<Text fontSize="sm" color="muted">
								Answer with the rate plans of the room, and the occupancies it
								may be priced at.
							</Text>
							<SubmissionError />
							<Divider sm />
							<Button type="submit" disabled={submitting}>
								{submitting ? "Fetching..." : "Fetch Rate Plans"}
							</Button>
							<SimulatorResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

const INVENTORY_PREFIX = "inventory_update_"

type TInventoryUpdateParams = {
	inventory_update_room_id: string
	inventory_update_start_date: Date
	inventory_update_end_date: Date
	inventory_update_free: number
}

const inventoryUpdateValidator = Validator.object().shape({
	inventory_update_room_id: Validator.string().required("Please select a room"),
	inventory_update_free: Validator.number()
		.required("Please provide the rooms left")
		.min(0, "Rooms left can not be negative"),
	...dateRangeValidators(INVENTORY_PREFIX),
})

function InventoryUpdateForm({ hotel }: { hotel: IHotel }) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TAxisRoomsResponse>()
	const [initialValues] = useState<TInventoryUpdateParams>(() => ({
		inventory_update_room_id: "",
		inventory_update_start_date: startOf(new Date(), "day"),
		inventory_update_end_date: startOf(new Date(), "day"),
		inventory_update_free: 5,
	}))
	if (!(hotel.ota_room_types || []).length) {
		return (
			<SimulatorCard
				title="Update Inventory"
				action={AXISROOMS_ACTIONS.INVENTORY_UPDATE}
			>
				<MissingOtaConfiguration what="rooms" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard
			title="Update Inventory"
			action={AXISROOMS_ACTIONS.INVENTORY_UPDATE}
		>
			<Form<TInventoryUpdateParams>
				initialValues={initialValues}
				validate={validateFormValues(inventoryUpdateValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendAxisRoomsRequest({
							xhr,
							action: AXISROOMS_ACTIONS.INVENTORY_UPDATE,
							payload: {
								data: {
									propertyId: hotel.uid,
									roomId: values.inventory_update_room_id,
									inventory: [
										{
											startDate: toApiDate(values.inventory_update_start_date),
											endDate: toApiDate(values.inventory_update_end_date),
											free: Number(values.inventory_update_free),
										},
									],
								},
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RoomTypeField hotel={hotel} prefix={INVENTORY_PREFIX} />
							<DateRangeFields prefix={INVENTORY_PREFIX} />
							<TextInputField
								label="Rooms Left"
								name="inventory_update_free"
								type="number"
								min={0}
								required
							/>
							<SubmissionError />
							<Divider sm />
							<Button type="submit" disabled={submitting}>
								{submitting ? "Sending..." : "Send Inventory"}
							</Button>
							<SimulatorResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

const RATE_PREFIX = "rate_update_"

type TRateUpdateParams = {
	rate_update_room_id: string
	rate_update_rate_plan_id: string
	rate_update_occupancy: string
	rate_update_amount: number
	rate_update_start_date: Date
	rate_update_end_date: Date
}

const rateUpdateValidator = Validator.object().shape({
	rate_update_room_id: Validator.string().required("Please select a room"),
	rate_update_rate_plan_id: Validator.string().required(
		"Please select a rate plan"
	),
	rate_update_occupancy: Validator.string().required(
		"Please select an occupancy"
	),
	rate_update_amount: Validator.number()
		.required("Please provide the rate")
		.min(0, "A rate can not be negative"),
	...dateRangeValidators(RATE_PREFIX),
})

function RateUpdateForm({ hotel }: { hotel: IHotel }) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TAxisRoomsResponse>()
	const [initialValues] = useState<TRateUpdateParams>(() => ({
		rate_update_room_id: "",
		rate_update_rate_plan_id: "",
		rate_update_occupancy: "DOUBLE",
		rate_update_amount: 5700,
		rate_update_start_date: startOf(new Date(), "day"),
		rate_update_end_date: startOf(new Date(), "day"),
	}))
	if (!(hotel.ota_rate_plans || []).length) {
		return (
			<SimulatorCard title="Update Rate" action={AXISROOMS_ACTIONS.RATE_UPDATE}>
				<MissingOtaConfiguration what="rate plans" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard title="Update Rate" action={AXISROOMS_ACTIONS.RATE_UPDATE}>
			<Form<TRateUpdateParams>
				initialValues={initialValues}
				validate={validateFormValues(rateUpdateValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendAxisRoomsRequest({
							xhr,
							action: AXISROOMS_ACTIONS.RATE_UPDATE,
							payload: {
								data: {
									propertyId: hotel.uid,
									roomId: values.rate_update_room_id,
									rateplanId: values.rate_update_rate_plan_id,
									rate: [
										{
											startDate: toApiDate(values.rate_update_start_date),
											endDate: toApiDate(values.rate_update_end_date),
											// the occupancy is the key AxisRooms price under
											[values.rate_update_occupancy]: Number(
												values.rate_update_amount
											),
										},
									],
								},
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RoomTypeField hotel={hotel} prefix={RATE_PREFIX} />
							<RatePlanField hotel={hotel} prefix={RATE_PREFIX} />
							<DateRangeFields prefix={RATE_PREFIX} />
							<SelectInputField
								label="Occupancy"
								name="rate_update_occupancy"
								required
							>
								{AXISROOMS_OCCUPANCIES.map((occupancy) => (
									<option value={occupancy} key={occupancy}>
										{occupancy}
									</option>
								))}
							</SelectInputField>
							<TextInputField
								label="Rate (before tax)"
								name="rate_update_amount"
								type="number"
								min={0}
								required
							/>
							<Text fontSize="xs" color="muted">
								An occupancy the room is not sold at is skipped rather than
								stored, and the rest of the call still goes through.
							</Text>
							<SubmissionError />
							<Divider sm />
							<Button type="submit" disabled={submitting}>
								{submitting ? "Sending..." : "Send Rate"}
							</Button>
							<SimulatorResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}

/**
 * What a minimum length of stay start at, so switching to it does not leave an
 * empty field behind.
 */
const DEFAULT_MINIMUM_NIGHTS = 1

/**
 * What a restriction is set to. A status, arrival or departure block is either
 * open or closed; a minimum length of stay is a number of nights.
 *
 * Switching between the two kinds drop the value, so nights are never sent as
 * the word "Close" and the other way round.
 */
function RestrictionValueField({ prefix }: { prefix: string }) {
	const { value: type } = useFieldValue<string>(`${prefix}type`)
	const { value, onChange } = useFieldValue<string>(`${prefix}value`)
	const isNights = type === "MLOS"
	const isNumeric = /^\d+$/.test(String(value ?? ""))

	useEffect(() => {
		if (isNights) {
			// nights are typed in, so a word left over from a block has to go.
			// It is replaced rather than emptied, so the field does not sit
			// there asking for something the caller never filled in
			if (!isNumeric) {
				onChange(String(DEFAULT_MINIMUM_NIGHTS))
			}
			return
		}
		// the list hold no empty option, so an empty value would show as the
		// first one while still failing validation
		if (value !== "Open" && value !== "Close") {
			onChange("Close")
		}
	}, [isNights, isNumeric, value, onChange])

	if (isNights) {
		return (
			<TextInputField
				label="Minimum Nights"
				name={`${prefix}value`}
				type="number"
				min={1}
				required
			/>
		)
	}

	return (
		<SelectInputField label="Value" name={`${prefix}value`} required>
			<option value="Open">Open</option>
			<option value="Close">Close</option>
		</SelectInputField>
	)
}

const RESTRICTION_PREFIX = "restriction_update_"

type TRestrictionUpdateParams = {
	restriction_update_room_id: string
	restriction_update_rate_plan_id: string
	restriction_update_type: string
	restriction_update_value: string
	restriction_update_start_date: Date
	restriction_update_end_date: Date
}

const restrictionUpdateValidator = Validator.object().shape({
	restriction_update_room_id: Validator.string().required(
		"Please select a room"
	),
	restriction_update_rate_plan_id: Validator.string().required(
		"Please select a rate plan"
	),
	restriction_update_type: Validator.string().required(
		"Please select a restriction"
	),
	// either Open/Close or a number of nights, so neither a string nor a
	// number validator fit on its own
	restriction_update_value: Validator.mixed().required(
		"Please provide the value"
	),
	...dateRangeValidators(RESTRICTION_PREFIX),
})

function RestrictionUpdateForm({ hotel }: { hotel: IHotel }) {
	const xhr = useXHR()
	const [response, setResponse] = useState<TAxisRoomsResponse>()
	const [initialValues] = useState<TRestrictionUpdateParams>(() => ({
		restriction_update_room_id: "",
		restriction_update_rate_plan_id: "",
		restriction_update_type: "Status",
		restriction_update_value: "Close",
		restriction_update_start_date: startOf(new Date(), "day"),
		restriction_update_end_date: startOf(new Date(), "day"),
	}))
	if (!(hotel.ota_rate_plans || []).length) {
		return (
			<SimulatorCard
				title="Update Restrictions"
				action={AXISROOMS_ACTIONS.RESTRICTION_UPDATE}
			>
				<MissingOtaConfiguration what="rate plans" />
			</SimulatorCard>
		)
	}
	return (
		<SimulatorCard
			title="Update Restrictions"
			action={AXISROOMS_ACTIONS.RESTRICTION_UPDATE}
		>
			<Form<TRestrictionUpdateParams>
				initialValues={initialValues}
				validate={validateFormValues(restrictionUpdateValidator)}
				onSubmit={withServerErrors(async (values) => {
					setResponse(
						await sendAxisRoomsRequest({
							xhr,
							action: AXISROOMS_ACTIONS.RESTRICTION_UPDATE,
							payload: {
								// restrictions come for several properties at once, each
								// nested by room and rate plan
								data: [
									{
										propertyId: hotel.uid,
										roomDetails: [
											{
												roomId: values.restriction_update_room_id,
												ratePlanDetails: [
													{
														ratePlanId: values.restriction_update_rate_plan_id,
														restrictions: {
															periods: [
																{
																	startDate: toApiDate(
																		values.restriction_update_start_date
																	),
																	endDate: toApiDate(
																		values.restriction_update_end_date
																	),
																},
															],
															type: values.restriction_update_type,
															value: values.restriction_update_value,
														},
													},
												],
											},
										],
									},
								],
							},
						})
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<RoomTypeField hotel={hotel} prefix={RESTRICTION_PREFIX} />
							<RatePlanField hotel={hotel} prefix={RESTRICTION_PREFIX} />
							<DateRangeFields prefix={RESTRICTION_PREFIX} />
							<SelectInputField
								label="Restriction"
								name="restriction_update_type"
								required
							>
								{AXISROOMS_RESTRICTION_TYPES.map((type) => (
									<option value={type} key={type}>
										{type}
									</option>
								))}
							</SelectInputField>
							<RestrictionValueField prefix={RESTRICTION_PREFIX} />
							<SubmissionError />
							<Divider sm />
							<Button type="submit" disabled={submitting}>
								{submitting ? "Sending..." : "Send Restriction"}
							</Button>
							<SimulatorResponse response={response} />
						</Stack>
					</form>
				)}
			</Form>
		</SimulatorCard>
	)
}
